import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/Button/Button';
import { AccountChip } from '../account/AccountChip';
import { SyncButton } from '../account/SyncButton';
import { useCurrentUser } from '../account/useAuth';
import { AppFooter } from '../branding/AppFooter';
import { fireBadgeEvent } from '../badges/badgesApi';
import { PrivacyNote } from '../components/PrivacyNote/PrivacyNote';
import { buildProjectFileName } from '../data/projectFile';
import type { Project } from '../data/types';
import { PublishDialog } from '../gallery/PublishDialog';
import { capture, register, shortHash } from '../lib/analytics';
import { useMySubmissions } from '../gallery/useGallery';
import { PublishStatusBadge } from './PublishStatusBadge';
import { observeDataset } from '../ml/datasetObservations';
import { observeFaceBias } from '../ml/faceBias';
import { IDLE_TRAINING } from '../stores/useProjectsStore';
import { useProjects, useProjectsStoreApi } from '../stores/projectsContext';
import { AudioPredictPanel } from './AudioPredictPanel';
import { ClassList } from './ClassList';
import { Coachmarks } from './Coachmarks';
import { DatasetObservations } from './DatasetObservations';
import { EvalPanel } from './EvalPanel';
import { NextActions } from './NextActions';
import { PosePredictPanel } from './PosePredictPanel';
import { PredictPanel } from './PredictPanel';
import { SampleModal } from './SampleModal';
import { TrainPanel } from './TrainPanel';
import { UndoToast } from './UndoToast';
import { useFaceBias } from './useFaceBias';
import './ProjectView.css';

interface LoadState {
  loading: boolean;
  project: Project | null;
  notFound: boolean;
}

export function ProjectView(): JSX.Element {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const store = useProjectsStoreApi();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({
    loading: true,
    project: null,
    notFound: false,
  });
  const [testing, setTesting] = useState(false);
  const [quizzing, setQuizzing] = useState(false);
  const [testedAt, setTestedAt] = useState<number | null>(null);
  const [savingFile, setSavingFile] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modalSample, setModalSample] = useState<{ classId: string; sampleId: string } | null>(
    null,
  );
  const [publishOpen, setPublishOpen] = useState(false);
  const { data: signedInUser } = useCurrentUser();
  const { data: mySubmissions } = useMySubmissions({ enabled: !!signedInUser });

  useEffect(() => {
    let cancelled = false;
    const found = store.getState().projects.find((p) => p.id === projectId);
    if (found) {
      setState({ loading: false, project: found, notFound: false });
      void store.getState().loadClasses(projectId);
      void store.getState().loadTrainedModel(projectId);
      void store.getState().loadEvalSamples(projectId);
      return;
    }
    void store
      .getState()
      .load()
      .then(() => {
        if (cancelled) return;
        const after = store.getState().projects.find((p) => p.id === projectId);
        setState({ loading: false, project: after ?? null, notFound: !after });
        if (after) {
          void store.getState().loadClasses(projectId);
          void store.getState().loadTrainedModel(projectId);
          void store.getState().loadEvalSamples(projectId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, store]);

  const classes = useProjects((s) => s.classesByProject[projectId]?.byId ?? []);
  const classesLoading = useProjects((s) => s.classesByProject[projectId]?.loading ?? false);
  const classesError = useProjects((s) => s.classesByProject[projectId]?.error ?? null);
  const samplesByClass = useProjects((s) => s.samplesByClass);
  const trainedModel = useProjects((s) => s.trainedModels[projectId] ?? null);
  const trainingState = useProjects((s) => s.trainingByProject[projectId] ?? IDLE_TRAINING);

  // Once we know the project, push its `kind` and short id into the
  // PostHog global properties so every event captured while this
  // view is open carries that context — no need to plumb it through
  // every individual `capture()` call.
  useEffect(() => {
    if (!state.project) return;
    register({
      project_kind: state.project.kind,
      project_local_id: shortHash(state.project.id),
    });
    capture('project_opened', {
      kind: state.project.kind,
      class_count: classes.length,
      has_trained_model: !!trainedModel,
      project_local_id: shortHash(state.project.id),
    });
    return () => {
      register({ project_kind: null, project_local_id: null });
    };
    // Intentionally only when the project identity changes — re-firing
    // on every class-count change would drown the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.project?.id]);

  // Phase 4.5b.9 — fire badge events for studio-side moments only the
  // frontend can see. Each is reported once per session via a ref so a
  // re-render doesn't re-fire (the backend de-dups too, but cheap to
  // skip on the client). Each call is fire-and-forget; signed-out kids
  // get a 401 that's swallowed. The PredictPanel handles the
  // "honest uncertainty" detection on its own ref.
  const trainedReportedRef = useRef(false);
  const testedReportedRef = useRef(false);
  useEffect(() => {
    if (trainedModel && !trainedReportedRef.current) {
      trainedReportedRef.current = true;
      fireBadgeEvent('trained_model');
    }
  }, [trainedModel]);
  const evalSamples = useProjects((s) => s.evalByProject[projectId]?.byId ?? []);
  const pendingDelete = useProjects((s) => s.pendingDelete);
  // Only class / samples toasts belong on this screen — project deletes
  // happen on Home and that's where their toast lives.
  const localPending =
    pendingDelete?.kind === 'class' && pendingDelete.projectId === projectId
      ? pendingDelete
      : pendingDelete?.kind === 'samples-batch'
        ? pendingDelete
        : null;

  // Whenever the class list changes, kick off sample loads for any class
  // we don't yet have a slice for. The store only loads once per class.
  useEffect(() => {
    for (const cls of classes) {
      if (samplesByClass[cls.id] === undefined) {
        void store.getState().loadSamples(cls.id);
      }
    }
  }, [classes, samplesByClass, store]);

  // Pose projects: warm up MoveNet on view mount so the live skeleton
  // overlay is visible the *instant* the kid opens any class's camera.
  // Without this preload the first frame would block on the dynamic
  // import of `@tensorflow-models/pose-detection` and the detector
  // boot, which is 1–2 s on a $200 Chromebook — long enough for the
  // kid to think nothing's happening.
  // Reads from `state` (instead of the destructured `project` later
  // in the function) so this effect can sit alongside the other
  // setup effects without a temporal-dead-zone error.
  useEffect(() => {
    if (state.project?.kind !== 'pose') return;
    void import('../ml/moveNet').then((m) => m.warmUpDetector?.()).catch(() => {});
  }, [state.project?.kind]);

  // Flatten the per-class slices into one project-wide sample list. The
  // observations and the face-bias detector both read this. Memoised so
  // the face detector hook's dependency stays stable when nothing changed.
  const allSamples = useMemo(
    () => classes.flatMap((c) => samplesByClass[c.id]?.byId ?? []),
    [classes, samplesByClass],
  );

  // Bias-awareness face counts for image projects only. Audio samples
  // can't have faces, and the detector wouldn't load on a non-image blob.
  const projectKind = state.project?.kind ?? null;
  const faceCounts = useFaceBias(allSamples, projectKind === 'image');

  if (state.loading) {
    return (
      <div className="bl-page">
        <div className="bl-project bl-project--centered">
          <p aria-live="polite">Opening your project…</p>
        </div>
        <AppFooter />
      </div>
    );
  }

  if (state.notFound || !state.project) {
    return (
      <div className="bl-page">
        <div className="bl-project bl-project--centered">
          <h1>Project not found</h1>
          <p>It might have been deleted. Head back home and pick another one.</p>
          <Button onClick={() => navigate('/studio')}>Back to studio</Button>
        </div>
        <AppFooter />
      </div>
    );
  }

  const { project } = state;
  const kindLabel = project.kind === 'image' ? 'Camera' : project.kind === 'audio' ? 'Mic' : 'Pose';

  const handleSaveProjectFile = async (): Promise<void> => {
    if (savingFile) return;
    setSavingFile(true);
    setSaveError(null);
    try {
      const blob = await store.getState().exportProjectFile(projectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildProjectFileName(project.name);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save your project file.');
    } finally {
      setSavingFile(false);
    }
  };

  return (
    <div className="bl-page">
      <div className="bl-project">
        <header className="bl-project__header">
          <button type="button" className="bl-project__back" onClick={() => navigate('/studio')}>
            ← Back to studio
          </button>
          <div className="bl-project__header-right">
            <button
              type="button"
              className="bl-project__save"
              onClick={() => void handleSaveProjectFile()}
              disabled={savingFile}
            >
              {savingFile ? 'Saving…' : 'Save project to file'}
            </button>
            <SyncButton
              projectId={project.id}
              projectName={project.name}
              projectKind={project.kind}
              blockProgram={project.blockProgram}
              serverId={project.serverId ?? null}
            />
            {signedInUser ? (
              <button
                type="button"
                className="bl-project__save"
                onClick={() => setPublishOpen(true)}
                // Disabled when there's nothing to publish OR the
                // account is suspended. Suspended kids can still see
                // their work locally; the backend would reject the
                // publish anyway, so disable upfront to avoid a
                // confusing mid-flow failure.
                disabled={classes.length === 0 || signedInUser.suspended}
                title={
                  signedInUser.suspended
                    ? "Your account is paused — you can't publish right now."
                    : undefined
                }
              >
                Publish to gallery
              </button>
            ) : (
              // Signed-out kids used to see no publish surface at all,
              // which made "how do I share my work?" invisible. The
              // nudge keeps the affordance discoverable and opens the
              // sign-in dialog (rendered by AccountChip in this
              // header). Browsing the gallery is still account-free.
              <button
                type="button"
                className="bl-project__signin-nudge"
                onClick={() => window.dispatchEvent(new CustomEvent('bryta:open-signin'))}
                title="Sign in to publish your project to the public gallery"
              >
                Sign in to publish to gallery →
              </button>
            )}
            <PrivacyNote state="on-device" />
            <AccountChip />
          </div>
        </header>
        {saveError && (
          <p role="alert" className="bl-project__error">
            {saveError}
          </p>
        )}

        <section className="bl-project__hero">
          <p className="bl-project__kind">{`${kindLabel} project`}</p>
          <div className="bl-project__title-row">
            <EditableProjectTitle
              value={project.name}
              onCommit={async (next) => {
                await store.getState().rename(projectId, next);
                // ProjectView keeps a local snapshot of the project
                // (`state.project`) so it doesn't re-render on every
                // store change — refresh it now so the new name
                // shows up immediately.
                const fresh = store.getState().projects.find((p) => p.id === projectId);
                if (fresh) {
                  setState((s) => ({ ...s, project: fresh }));
                }
              }}
            />
            {project.remixedAt !== undefined && (
              <span
                className="bl-project__remix-badge"
                title="This project started as a remix of someone else's gallery submission."
              >
                Remixed
              </span>
            )}
            {/* Submission status moved out of the header-right action
                row — its uppercase pill broke the row's wrap rhythm
                whenever an "awaiting_…" status pushed the layout
                past one line. It belongs next to the project name
                anyway since it's per-project status, not a global
                action. */}
            {signedInUser && mySubmissions && (
              <PublishStatusBadge
                submissions={mySubmissions}
                projectName={project.name}
                projectKind={project.kind}
              />
            )}
          </div>
        </section>

        <div
          className={`bl-project__columns${
            (testing || quizzing) && trainedModel ? ' bl-project__columns--with-preview' : ''
          }`}
        >
          <div className="bl-project__col bl-project__col--classes">
            <ClassList
              classes={classes}
              kind={project.kind}
              loading={classesLoading}
              error={classesError}
              samplesByClass={samplesByClass}
              onAdd={async (name) => {
                await store.getState().addClass(projectId, name);
              }}
              onRename={(id, name) => store.getState().renameClass(projectId, id, name)}
              onUpdateCaptureSettings={(id, settings) =>
                store.getState().setClassCaptureSettings(projectId, id, settings)
              }
              onToggleDisabled={(id, disabled) =>
                store.getState().setClassDisabled(projectId, id, disabled)
              }
              onDelete={(id) => store.getState().scheduleClassDelete(projectId, id)}
              onRemoveAllSamples={(classId) => store.getState().scheduleSamplesDelete(classId)}
              onAddSample={async (classId, blob, opts) => {
                // Forward `opts` (carries pose keypoints from WebcamPanel
                // captures) so the store can persist them alongside the
                // blob. Dropping `opts` here was the bug that left pose
                // samples without their MoveNet skeleton overlay.
                await store.getState().addSample(classId, blob, opts);
              }}
              onDeleteSample={(classId, sampleId) =>
                store.getState().deleteSample(classId, sampleId)
              }
              onOpenSample={(classId, sampleId) => setModalSample({ classId, sampleId })}
            />

            <DatasetObservations
              observations={[
                ...observeDataset(classes, allSamples, project.kind),
                ...(project.kind === 'image'
                  ? observeFaceBias(classes, allSamples, faceCounts)
                  : []),
              ]}
            />
          </div>

          <div className="bl-project__col bl-project__col--training">
            <TrainPanel
              classes={classes}
              kind={project.kind}
              samples={classes.flatMap((c) => samplesByClass[c.id]?.byId ?? [])}
              trained={trainedModel}
              training={trainingState}
              onTrain={(hyperparams) => {
                // Closing any open prediction / quiz panel before re-training so
                // the loaded model gets disposed and the kid can re-test cleanly
                // once training finishes.
                setTesting(false);
                setQuizzing(false);
                void store.getState().trainProject(projectId, hyperparams);
              }}
              onCancel={() => store.getState().cancelTraining(projectId)}
              onTest={() => {
                setQuizzing(false);
                setTesting(true);
                setTestedAt(Date.now());
              }}
              {...(project.kind === 'image'
                ? {
                    onQuiz: () => {
                      setTesting(false);
                      setQuizzing(true);
                      // Quiz mode uses eval samples — data the kid
                      // captured separately from training. That's the
                      // "held-out" definition the badge celebrates.
                      if (!testedReportedRef.current) {
                        testedReportedRef.current = true;
                        fireBadgeEvent('tested_on_held_out');
                      }
                    },
                  }
                : {})}
            />
          </div>

          {(testing || quizzing) && trainedModel && (
            <div className="bl-project__col bl-project__col--preview">
              {testing && project.kind === 'image' && (
                <PredictPanel
                  projectId={projectId}
                  labels={trainedModel.labels}
                  onClose={() => setTesting(false)}
                  nextActions={
                    <NextActions
                      onOpenWebsite={() => navigate(`/studio/p/${projectId}/publish`)}
                      onOpenAddToGallery={() => setPublishOpen(true)}
                      onExport={() => store.getState().exportProject(projectId)}
                    />
                  }
                />
              )}
              {testing && project.kind === 'audio' && (
                <AudioPredictPanel
                  projectId={projectId}
                  labels={trainedModel.labels}
                  onClose={() => setTesting(false)}
                  nextActions={
                    <NextActions
                      onOpenWebsite={() => navigate(`/studio/p/${projectId}/publish`)}
                      onOpenAddToGallery={() => setPublishOpen(true)}
                      onExport={() => store.getState().exportProject(projectId)}
                    />
                  }
                />
              )}
              {testing && project.kind === 'pose' && (
                <PosePredictPanel
                  projectId={projectId}
                  labels={trainedModel.labels}
                  onClose={() => setTesting(false)}
                  nextActions={
                    <NextActions
                      onOpenWebsite={() => navigate(`/studio/p/${projectId}/publish`)}
                      onOpenAddToGallery={() => setPublishOpen(true)}
                      onExport={() => store.getState().exportProject(projectId)}
                    />
                  }
                />
              )}

              {/* `EvalPanel` is image-only — it loads `browserImageTrainer`
                  and predicts on image blobs. The `onQuiz` callback is
                  already gated above so `quizzing` only flips true for
                  image kinds, but we mirror the kind-check here for
                  defense in depth and to match the test-panel pattern. */}
              {quizzing && project.kind === 'image' && (
                <EvalPanel
                  projectId={projectId}
                  trained={trainedModel}
                  samples={evalSamples}
                  onAddEvalSample={(classId, blob) =>
                    store.getState().addEvalSample(projectId, classId, blob)
                  }
                  onDeleteEvalSample={(sampleId) =>
                    store.getState().deleteEvalSample(projectId, sampleId)
                  }
                  onClose={() => setQuizzing(false)}
                />
              )}
            </div>
          )}
        </div>

        {localPending?.kind === 'class' && (
          <UndoToast
            message={`Deleted class "${localPending.class.name}"`}
            expiresAt={localPending.expiresAt}
            onUndo={() => store.getState().undoDelete()}
          />
        )}
        {localPending?.kind === 'samples-batch' && (
          <UndoToast
            message={`Removed ${localPending.samples.length} ${
              localPending.samples.length === 1 ? 'sample' : 'samples'
            }`}
            expiresAt={localPending.expiresAt}
            onUndo={() => store.getState().undoDelete()}
          />
        )}

        {modalSample && (
          <SampleModal
            classes={classes}
            samplesByClass={Object.fromEntries(
              classes.map((c) => [c.id, samplesByClass[c.id]?.byId ?? []]),
            )}
            kind={project.kind}
            classId={modalSample.classId}
            sampleId={modalSample.sampleId}
            onFocus={(next) => setModalSample(next)}
            onClose={() => setModalSample(null)}
            onDelete={async (classId, sampleId) => {
              await store.getState().deleteSample(classId, sampleId);
            }}
            onReassign={async (fromClassId, sampleId, toClassId) => {
              await store.getState().reassignSample(fromClassId, sampleId, toClassId);
            }}
          />
        )}

        <Coachmarks
          classCount={classes.length}
          sampleCount={classes.reduce((n, c) => n + (samplesByClass[c.id]?.byId.length ?? 0), 0)}
          trained={Boolean(trainedModel)}
          testedAt={testedAt}
        />

        <PublishDialog
          isOpen={publishOpen}
          project={project}
          classes={classes}
          samplesByClass={samplesByClass}
          onClose={() => setPublishOpen(false)}
        />
      </div>
      <AppFooter />
    </div>
  );
}

const PROJECT_NAME_MAX = 120;

interface EditableProjectTitleProps {
  value: string;
  onCommit: (next: string) => Promise<void> | void;
}

/** Inline-editable project title. Click the heading (or the pencil)
 *  to edit. Enter / blur commits, Escape cancels. The input is
 *  trimmed and capped at the same 120-char limit the create flow
 *  uses; an unchanged or empty submit is a no-op. */
function EditableProjectTitle({ value, onCommit }: EditableProjectTitleProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Stay in sync with the store when the title changes from
  // somewhere else (e.g. a successful rename in another tab).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [editing, value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = (): void => {
    const trimmed = draft.trim().slice(0, PROJECT_NAME_MAX);
    if (trimmed && trimmed !== value) {
      void onCommit(trimmed);
    }
    setEditing(false);
  };

  const cancel = (): void => {
    setDraft(value);
    setEditing(false);
  };

  // Editing mode swaps the heading for an input. View mode shows
  // the heading + a pencil button next to it — same pattern the
  // class rename uses, so the kid sees a consistent affordance.
  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className="bl-project__title bl-project__title--input"
        aria-label="Project name"
        value={draft}
        maxLength={PROJECT_NAME_MAX}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }

  return (
    <>
      <h1 className="bl-project__title">{value}</h1>
      <button
        type="button"
        className="bl-project__title-rename"
        onClick={() => setEditing(true)}
        aria-label={`Rename project ${value}`}
        title="Rename this project"
      >
        <span aria-hidden="true">✎</span>
      </button>
    </>
  );
}
